import { defineStore, storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useConfigStore } from '@dashboard/stores/configStore'

/**
 * The hostname a domain entry is saved as, or null when it isn't a domain.
 * Mirrors the server's `ProjectDomains::hostname`: a bare hostname or an
 * http(s) URL with no page path, port or credentials, stored lowercase.
 * Unlike the project website field, `www.` is kept: it is a separate host.
 */
export const domainNameFromInput = (input: string): string | null => {
  const value = input.trim()
  const match = /^(?:https?:\/\/)?([^/?#:@\\\s]+)\/?$/i.exec(value)
  const host = match?.[1]
  if (!host || !/^[\p{L}\p{M}\p{N}-]+(?:\.[\p{L}\p{M}\p{N}-]+)+$/u.test(host)) {
    return null
  }
  let hostname: string
  try {
    hostname = new URL(`https://${host}`).hostname
  } catch {
    return null
  }
  const labels = hostname.split('.')
  const tld = labels[labels.length - 1] ?? ''
  if (
    hostname.length > 253 ||
    labels.some((label) => !/^[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?$/.test(label)) ||
    /^(?:\d+|0x[\da-f]+)$/.test(tld)
  ) {
    return null
  }
  return hostname
}

/** The draft config's domain list. The first domain is the primary one. */
export const useDomainNameStore = defineStore('domainName', () => {
  const configStore = useConfigStore()
  const { config } = storeToRefs(configStore)

  const domains = computed<string[]>(() => config.value?.domainNames ?? [])

  const has = (name: string) => domains.value.some((domain) => domain.toLowerCase() === name.toLowerCase())

  const replace = (next: string[]) => {
    if (config.value) {
      config.value.domainNames = next
    }
  }

  /** Appends a domain; returns false when it is already listed or there is no config. */
  const create = ({ name }: { name: string }): boolean => {
    if (!config.value || has(name)) {
      return false
    }
    replace([...domains.value, name])
    return true
  }

  const destroy = (name: string) => {
    replace(domains.value.filter((domain) => domain !== name))
  }

  /** Moves a listed domain to the front, keeping the others in order. */
  const makePrimary = (name: string) => {
    if (!domains.value.includes(name)) {
      return
    }
    replace([name, ...domains.value.filter((domain) => domain !== name)])
  }

  return {
    domains,
    has,
    create,
    destroy,
    makePrimary
  }
})
